import { PageTemplate, Region, SUBREGION_KIND, Suburb } from '../../../../models/content';

/**
 * The suburb page template's rules on the client (#262; plan §7.5). Every suburb, place and sub-region page is one
 * template filled from its record, plus blocks inserted on that page only. conductor-server (page-template.ts) and
 * picaluna-v3 (src/lib/suburbTemplate.ts) implement the anchor rule too; anchor-cases.json is the server's table
 * (scripts/fixtures/suburb-template-anchor-cases.json), copied here so all three are tested against the same cases.
 */

export type TemplateKey = 'suburb' | 'subregion';

export const TEMPLATE_KEYS: ReadonlyArray<{ key: TemplateKey; label: string }> = [
  { key: 'suburb', label: 'Suburb page' },
  { key: 'subregion', label: 'Sub-region page' },
];

/** Each template is one record with a fixed id (the server refuses any other). */
export const templateRecordId = (key: TemplateKey): string => `tpl-${key}`;

/**
 * The stored template of a kind from the account's content.pageTemplates, or null (none, or removed). The cached
 * account holds its collections as lists (AccountService runs objectsToArrays on what it loads), a write's response
 * may hold them keyed by id: find the record by its id in either.
 */
export function storedTemplate(
  raw: Record<string, PageTemplate> | PageTemplate[] | null | undefined,
  key: TemplateKey
): PageTemplate | null {
  const id = templateRecordId(key);
  const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];
  const t = list.find((x) => x && typeof x === 'object' && x._meta?.id === id);
  return t && !(t._meta as { deleted?: boolean }).deleted ? t : null;
}

/** An inserted block's anchor meaning "after the last block". */
export const END = '_end';

/**
 * Blocks a template may hold besides its hero and text blocks (the server's TEMPLATE_COMPONENTS). Region-wide repeats
 * (Funeral Costs, the directors grid, venues lists) and page content (panels, galleries) stay off templates: shared
 * blocks are what make 195 pages near-copies.
 */
export const TEMPLATE_COMPONENTS: ReadonlyArray<string> = [
  'suburb-places',
  'suburbs-list',
  'subregions-list',
  'suburb-subregions',
  'faq-overview',
  'suburb-arrangers',
  'suburb-families',
  'phone-cta',
];

/** The template a record's page is built from: sub-regions have their own, everything else is a suburb page. */
export function templateKeyFor(record: Partial<Suburb> | null | undefined): TemplateKey {
  return record?.kind === SUBREGION_KIND ? 'subregion' : 'suburb';
}

// ------------------------------------------------------------------ placeholders

/** The placeholders a template and an inserted block may use, for the "Insert placeholder" menu. */
export const PAGE_TOKENS: ReadonlyArray<{ token: string; label: string; example: string }> = [
  { token: '{{suburb:name}}', label: "The page's name", example: 'Randwick' },
  { token: '{{suburb:where}}', label: 'Its name in a sentence', example: 'the Eastern Suburbs' },
  { token: '{{suburb:state}}', label: 'Its state', example: 'NSW' },
  { token: '{{region:name}}', label: "Its region's name", example: 'Sydney' },
  { token: '{{region:path}}', label: "Its region page's address", example: '/nsw/sydney' },
];

/**
 * The placeholders a link target may hold: only the region page's address, optionally followed by a path. A name is
 * not an address, and the server (like the site's publish check) refuses any other placeholder in a target.
 */
export const TARGET_TOKENS = PAGE_TOKENS.filter((t) => t.token === '{{region:path}}');
const REGION_PATH = /^\{\{\s*region\s*:\s*path\s*\}\}/;

/** A target with the region page's address put first: a path already typed follows it, anything else gives way. */
export function withTargetToken(target: string | null | undefined, token: string): string {
  const rest = (target ?? '').trim().replace(REGION_PATH, '');
  return rest.startsWith('/') && !rest.startsWith('//') ? `${token}${rest}` : token;
}

/** #222's grammar (picaluna-v3 resolveTokens DOUBLE). */
const DOUBLE = /\{\{\s*([a-z]+)\s*:\s*([A-Za-z0-9._-]+)\s*\}\}/g;

export interface PageTokenValues {
  name: string;
  where: string;
  state: string;
  regionName: string;
  regionPath: string;
}

/** One page's placeholder values (the server's pageTokenValues). */
export function previewTokens(
  record: Partial<Suburb> | null | undefined,
  region: Partial<Region> | null | undefined
): PageTokenValues {
  const state = String(region?.state || 'NSW').toUpperCase();
  const name = String(record?.name || '');
  return {
    name,
    where: (typeof record?.inSentence === 'string' && record.inSentence.trim()) || name,
    state,
    regionName: String(region?.name || ''),
    regionPath: region?.slug ? `/${state.toLowerCase()}/${region.slug}` : '',
  };
}

/** Fill in the page placeholders; other token types ({{copy:…}}, {{cost:…}}) are the site's own pass. */
export function resolvePageTokens(text: string | null | undefined, v: PageTokenValues): string {
  if (typeof text !== 'string') return '';
  const values: Record<string, string> = {
    'suburb:name': v.name,
    'suburb:where': v.where,
    'suburb:state': v.state,
    'region:name': v.regionName,
    'region:path': v.regionPath,
  };
  return text.replace(DOUBLE, (whole, type, arg) => values[`${type}:${arg}`] ?? whole);
}

/** A placeholder put in at the caret (or over the selection); the caret then sits after it. */
export function insertToken(
  text: string | null | undefined,
  token: string,
  start?: number | null,
  end?: number | null
): { text: string; caret: number } {
  const value = text ?? '';
  const from = clamp(start ?? value.length, value.length);
  const to = Math.max(from, clamp(end ?? from, value.length));
  return { text: value.slice(0, from) + token + value.slice(to), caret: from + token.length };
}

function clamp(n: number, max: number): number {
  return Math.min(Math.max(0, n), max);
}

// ------------------------------------------------------------------ blocks and the anchor rule

interface Indexed {
  _meta?: { id?: string; index?: number; deleted?: boolean };
}

const isDeleted = (b: Indexed) => b._meta?.deleted === true;
const indexOf = (b: Indexed | undefined) =>
  typeof b?._meta?.index === 'number' ? b._meta.index : Number.MAX_SAFE_INTEGER;
const byIndexThenId = <T extends { id: string; block: Indexed }>(a: T, b: T) =>
  indexOf(a.block) - indexOf(b.block) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** A stored block collection (keyed by id, or a list) as [id, block] pairs; arrays don't survive the EAV round trip. */
export function blockEntries<T extends Indexed>(
  raw: Record<string, T> | T[] | null | undefined
): Array<{ id: string; block: T }> {
  const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];
  return list
    .filter((b): b is T => !!b && typeof b === 'object' && !!b._meta?.id)
    .map((block) => ({ id: block._meta!.id as string, block }));
}

/** A template's blocks in template order, removed ones (tombstones) included. */
export function templateOrder(
  template: Pick<PageTemplate, 'blocks'> | null | undefined
): Array<{ id: string; block: PageTemplate['blocks'][string]; removed: boolean }> {
  return blockEntries(template?.blocks)
    .sort(byIndexThenId)
    .map(({ id, block }) => ({ id, block, removed: isDeleted(block) }));
}

export interface PlacedBlock<T> {
  id: string;
  source: 'template' | 'inserted';
  block: T;
}

/**
 * The anchor rule (the server's placeBlocks): live template blocks in order, each followed by the blocks inserted
 * after it.
 *   - a live anchor: right after it, even where that block is hidden on this page;
 *   - a removed anchor: after the nearest live block before it, so the insertion stays where the removed block was
 *     (after the blocks inserted at that live block itself);
 *   - _end, or an anchor the template doesn't have: at the end.
 * Several at one anchor keep their _meta.index order.
 */
export function placeBlocks<T extends Indexed & { after?: string }>(
  template: Pick<PageTemplate, 'blocks'> | null | undefined,
  blocks: Record<string, T> | T[] | null | undefined
): Array<PlacedBlock<T | PageTemplate['blocks'][string]>> {
  const order = templateOrder(template);
  // Each anchor's slot: the live block it lands after (null = the end) and its rank among the anchors there.
  const slot = new Map<string, { at: string | null; rank: number }>();
  let lastLive: string | null = null;
  order.forEach((t, rank) => {
    if (!t.removed) lastLive = t.id;
    slot.set(t.id, { at: lastLive, rank });
  });
  const groups = new Map<string | null, Array<{ id: string; block: T; rank: number }>>();
  blockEntries(blocks)
    .filter(({ block }) => !isDeleted(block))
    .sort(byIndexThenId)
    .forEach(({ id, block }) => {
      const s = block.after && block.after !== END ? slot.get(block.after) : undefined;
      const key = s ? s.at : null;
      if (!groups.has(key)) groups.set(key, []);
      // A stable sort by rank keeps index order within one rank.
      groups.get(key)!.push({ id, block, rank: s ? s.rank : Number.MAX_SAFE_INTEGER });
    });
  const take = (key: string | null) =>
    (groups.get(key) ?? [])
      .sort((x, y) => x.rank - y.rank)
      .map((g) => ({ id: g.id, source: 'inserted' as const, block: g.block }));
  const out: Array<PlacedBlock<T | PageTemplate['blocks'][string]>> = [];
  for (const t of order) {
    if (t.removed) continue;
    out.push({ id: t.id, source: 'template', block: t.block });
    out.push(...take(t.id));
  }
  out.push(...take(null));
  return out;
}

/** How many records have blocks placed after each of these template blocks (the warning before one is removed). */
export function insertionsAfter(
  records: ReadonlyArray<Partial<Suburb>>,
  blockIds: ReadonlyArray<string>
): { records: number; blocks: number } {
  const ids = new Set(blockIds);
  let recs = 0;
  let blocks = 0;
  for (const r of records) {
    const n = blockEntries<Indexed & { after: string }>(r.blocks).filter(
      ({ block }) => !isDeleted(block) && ids.has(block.after)
    ).length;
    if (n) recs++;
    blocks += n;
  }
  return { records: recs, blocks };
}
